/* Community project archive: expandable summaries at every viewport width. */
(() => {
  'use strict';

  const selector = '[data-community-projects][data-project-view="page"]';
  const namespace = '__pinmeCommunityProjects';
  if (window[namespace]) {
    window[namespace].mount(document);
    return;
  }

  const controllers = new Map();
  const copyText = value => { const text = String(value ?? '').trim(); return text === '-' ? '' : text; };

  function rootsWithin(scope) {
    const roots = Array.from(scope.querySelectorAll(selector));
    if (scope.matches?.(selector)) roots.unshift(scope);
    return roots;
  }

  function init(section) {
    if (controllers.has(section)) return;
    const abort = new AbortController();
    const { signal } = abort;
    const articles = Array.from(section.querySelectorAll('[data-project-id]'));
    const projects = new Map(articles.map(article => [article.dataset.projectId, {
      article,
      detail: article.querySelector('[data-project-detail]'),
      toggle: article.querySelector('[data-project-toggle]'),
      title: article.querySelector('.pc-card__title')?.textContent.trim() || article.dataset.projectId,
      status: article.dataset.voteStatus
    }]));
    if (!projects.size) return;

    // Newly added project records use the same small disclosure control.
    const usesStudioTriangles = section.hasAttribute('data-community-studio');
    projects.forEach(project => {
      if (!project.toggle) return;
      let icon = project.toggle.querySelector('.pc-toggle-icon');
      if (!icon) {
        icon = document.createElement('span');
        icon.className = 'pc-toggle-icon';
        icon.setAttribute('aria-hidden', 'true');
        project.toggle.append(icon);
      }
      if (usesStudioTriangles && !icon.querySelector('svg')) {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('viewBox', '0 0 16 16');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', 'M4 2 L12 8 L4 14 Z');
        path.setAttribute('fill', 'currentColor');
        svg.append(path);
        icon.replaceChildren(svg);
      } else if (!usesStudioTriangles) {
        icon.textContent = '+';
      }
    });

    const media = window.matchMedia('(max-width: 767px)');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const filters = Array.from(section.querySelectorAll('[data-project-filter]'));
    const empty = section.querySelector('[data-project-empty]');
    const board = section.querySelector('.pc-board');
    const opened = new Set();
    let selected = null;
    let filter = filters.find(button => button.getAttribute('aria-pressed') === 'true')?.dataset.projectFilter || 'all';
    let scrollFrame;
    let disposed = false;
    let initialLinkPending = false;
    let initialFontsReady = false;

    function idFromLocation() {
      const url = new URL(window.location.href);
      const hashMatch = url.hash.match(/^#project-(.+)$/);
      const queryId = url.searchParams.get('project');
      if (hashMatch && projects.has(hashMatch[1])) return hashMatch[1];
      if (projects.has(queryId)) return queryId;
      return null;
    }

    const requestedId = idFromLocation();
    if (requestedId) {
      selected = requestedId;
      opened.add(requestedId);
      initialLinkPending = true;
    }

    function eligibleIds() {
      return Array.from(projects).filter(([, project]) => filter === 'all' || project.status === filter).map(([id]) => id);
    }

    function focusProject(id) {
      const project = projects.get(id);
      if (project && !project.article.hidden) project.toggle?.focus({ preventScroll: true });
    }

    function updateCounts() {
      filters.forEach(button => {
        const value = button.dataset.projectFilter;
        const count = value === 'all' ? projects.size : Array.from(projects.values()).filter(project => project.status === value).length;
        const label = button.querySelector('[data-project-count]') || button.querySelector('span');
        if (label) label.textContent = String(count);
      });
    }

    function render({ recoverFocus = true } = {}) {
      const active = document.activeElement;
      const eligible = eligibleIds();
      filters.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.projectFilter === filter)));
      if (empty) empty.hidden = eligible.length > 0;
      if (board) board.hidden = eligible.length === 0;

      projects.forEach((project, id) => {
        const visible = eligible.includes(id);
        const open = visible && opened.has(id);
        project.article.hidden = !visible;
        project.article.classList.toggle('is-open', open);
        project.article.removeAttribute('role');
        project.article.removeAttribute('aria-labelledby');
        if (project.detail) {
          if (open) project.detail.querySelectorAll('image[data-project-src]').forEach(image => {
            image.setAttribute('href', image.dataset.projectSrc);
            image.removeAttribute('data-project-src');
          });
          project.detail.hidden = !open;
        }
        if (project.toggle) {
          project.toggle.setAttribute('aria-expanded', String(open));
          const key = open ? 'projectOpenLabel' : 'projectClosedLabel', fallback = open ? 'Hide details' : 'View details';
          const action = copyText(Object.hasOwn(project.toggle.dataset, key) ? project.toggle.dataset[key] : fallback);
          project.toggle.setAttribute('aria-label', `${action || fallback}: ${project.title}`);
          const label = project.toggle.querySelector('[data-project-action-label]');
          if (label) { label.textContent = action; label.hidden = !action; }
          const icon = project.toggle.querySelector('.pc-toggle-icon');
          if (icon && !usesStudioTriangles) icon.textContent = open ? '−' : '+';
        }
      });

      if (!recoverFocus || !section.contains(active) || !active.closest?.('[hidden]')) return;
      const visibleOpen = eligible.find(id => opened.has(id));
      if (eligible.length) focusProject(visibleOpen || eligible[0]);
      else section.querySelector('[data-project-reset]')?.focus({ preventScroll: true });
    }

    function updateLocation(id) {
      const next = new URL(window.location.href);
      if (id) {
        next.searchParams.set('project', id);
        next.hash = `project-${id}`;
      } else {
        next.searchParams.delete('project');
        if (next.hash.startsWith('#project-')) next.hash = '';
      }
      window.history.replaceState(window.history.state, '', next);
    }

    function toggleProject(id) {
      if (!eligibleIds().includes(id)) return;
      const wasOpen = opened.has(id);
      if (wasOpen) opened.delete(id);
      else {
        if (media.matches) opened.clear();
        opened.add(id);
      }
      selected = id;
      const stillOpen = Array.from(opened).filter(openId => eligibleIds().includes(openId));
      updateLocation(wasOpen ? stillOpen.at(-1) || null : id);
      render();
      if (!wasOpen) {
        if (scrollFrame) cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => {
          scrollFrame = requestAnimationFrame(() => {
            const project = projects.get(id);
            if (!disposed && opened.has(id)) (project?.detail || project?.article)?.scrollIntoView({ block: 'start', behavior: reduced.matches ? 'instant' : 'smooth' });
          });
        });
      }
    }

    function closeProject(id) {
      opened.delete(id);
      updateLocation(Array.from(opened).at(-1) || null);
      render({ recoverFocus: false });
      focusProject(id);
    }

    function roundSelection(rounds, value, { focus = false } = {}) {
      const tabs = Array.from(rounds.querySelectorAll('[data-vote-tab]'));
      const chosen = tabs.find(tab => tab.dataset.voteTab === value);
      if (!chosen) return;
      tabs.forEach(tab => {
        const active = tab === chosen;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      rounds.querySelectorAll('[data-vote-panel]').forEach(panel => { panel.hidden = panel.dataset.votePanel !== value; });
      if (focus) chosen.focus({ preventScroll: true });
    }

    function projectImage(button) {
      const img = button.querySelector('img');
      const svg = Array.from(button.querySelectorAll('svg')).find(item => item.getClientRects().length);
      const source = svg?.querySelector('image');
      return {
        src: img?.dataset.projectOriginal || img?.src || source?.getAttribute('href') || source?.getAttribute('xlink:href'),
        alt: img?.alt || svg?.getAttribute('aria-label') || '',
        caption: button.closest('.pv-candidate')?.querySelector('.pv-candidate__name')?.textContent.trim() || button.closest('figure')?.querySelector('figcaption')?.textContent.trim() || ''
      };
    }

    function openProjectImage(button) {
      if (!window.PinmeStudioMedia?.open) return;
      const group = button.closest('.pv-candidates, .pc-release-comparison') || button;
      const buttons = Array.from(group.querySelectorAll('[data-project-image]'));
      const items = buttons.map(trigger => ({ trigger, ...projectImage(trigger) })).filter(item => item.src);
      const index = items.findIndex(item => item.trigger === button);
      if (index >= 0) window.PinmeStudioMedia.open(items, index, button);
    }

    section.addEventListener('click', event => {
      const button = event.target.closest?.('button');
      if (!button || !section.contains(button)) return;
      // A delayed font load must never pull the view back after interaction.
      initialLinkPending = false;
      if (button.hasAttribute('data-project-image')) {
        event.preventDefault();
        openProjectImage(button);
      } else if (button.hasAttribute('data-project-filter')) {
        filter = button.dataset.projectFilter;
        render();
      } else if (button.hasAttribute('data-project-reset')) {
        filter = 'ended';
        render();
        filters.find(item => item.dataset.projectFilter === 'ended')?.focus({ preventScroll: true });
      } else if (button.hasAttribute('data-project-toggle')) {
        toggleProject(button.dataset.projectToggle);
      } else if (button.hasAttribute('data-project-close')) {
        closeProject(button.dataset.projectClose);
      } else if (button.hasAttribute('data-vote-tab')) {
        const rounds = button.closest('[data-vote-rounds]');
        if (rounds) roundSelection(rounds, button.dataset.voteTab);
      }
    }, { signal });

    section.addEventListener('keydown', event => {
      const button = event.target.closest?.('button[data-vote-tab]');
      if (!button) return;
      const rounds = button.closest('[data-vote-rounds]');
      if (!rounds) return;
      const tabs = Array.from(rounds.querySelectorAll('[data-vote-tab]'));
      const index = tabs.indexOf(button);
      let next;
      if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = tabs.length - 1;
      else if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
      else return;
      event.preventDefault();
      initialLinkPending = false;
      roundSelection(rounds, tabs[next].dataset.voteTab, { focus: true });
    }, { signal });

    section.querySelectorAll('[data-vote-rounds]').forEach(rounds => {
      const active = rounds.querySelector('[data-vote-tab][aria-selected="true"]') || rounds.querySelector('[data-vote-tab]');
      if (active) roundSelection(rounds, active.dataset.voteTab);
    });

    function cancelInitialAlignment() {
      initialLinkPending = false;
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
    }

    function alignInitialProject() {
      if (!initialLinkPending || !initialFontsReady || disposed) return;
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = requestAnimationFrame(() => {
          const project = projects.get(selected);
          const article = project?.article;
          if (disposed || !initialLinkPending || !section.isConnected || !article || article.hidden) return;
          initialLinkPending = false;
          (project.detail || article).scrollIntoView({ block: 'start', behavior: 'instant' });
        });
      });
    }

    const onModeChange = () => {
      if (media.matches && opened.size > 1) {
        const keep = selected && opened.has(selected) ? selected : Array.from(opened).at(-1);
        opened.clear();
        if (keep) opened.add(keep);
      }
      render();
      alignInitialProject();
    };
    if (media.addEventListener) media.addEventListener('change', onModeChange, { signal });
    else {
      media.addListener(onModeChange);
      signal.addEventListener('abort', () => media.removeListener(onModeChange), { once: true });
    }

    function syncLocation() {
      const id = idFromLocation();
      opened.clear();
      if (id) {
        opened.add(id);
        selected = id;
        filter = 'all';
      }
      render();
    }
    window.addEventListener('popstate', syncLocation, { signal });
    window.addEventListener('hashchange', syncLocation, { signal });

    const destroy = () => {
      disposed = true;
      abort.abort();
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
      controllers.delete(section);
    };
    controllers.set(section, { destroy });
    updateCounts();
    render({ recoverFocus: false });

    if (requestedId) {
      ['pointerdown', 'wheel', 'touchstart', 'keydown'].forEach(type => {
        window.addEventListener(type, cancelInitialAlignment, { capture: true, passive: true, signal });
      });
      window.addEventListener('load', alignInitialProject, { once: true, signal });
      Promise.resolve(document.fonts?.ready).catch(() => {}).then(() => {
        initialFontsReady = true;
        alignInitialProject();
      });
    }
  }

  const api = {
    mount(scope = document) { rootsWithin(scope).forEach(init); },
    destroy(scope = document) { rootsWithin(scope).forEach(section => controllers.get(section)?.destroy()); }
  };
  window[namespace] = api;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => api.mount(), { once: true });
  else api.mount();
  document.addEventListener('shopify:section:load', event => api.mount(event.target));
  document.addEventListener('shopify:section:unload', event => api.destroy(event.target));
})();
